import React from 'react';
import { useTranslation } from 'react-i18next';
import './RadarChart.css';

interface DimensionDetail {
    cefr: string;
    score: number;
    trend: string;
    dataPoints: number;
}

interface RadarChartProps {
    data: {
        vocabularyMastery: number;
        grammarAccuracy: number;
        sentenceStructure: number;
        listening: number | null;
        speaking: number | null;
        writing: number | null;
    };
    labels: string[];
    size?: number;
    /** Per-dimension CEFR details (enables colored dots + CEFR labels) */
    dimensionDetails?: {
        vocabulary: DimensionDetail;
        grammar: DimensionDetail;
        sentence: DimensionDetail;
        listening: DimensionDetail;
        speaking: DimensionDetail;
        writing: DimensionDetail;
    };
    /** Overall confidence 0-1 */
    confidence?: number;
    /** Total data points */
    dataPoints?: number;
    /** Overall CEFR level to display in center */
    overallCefr?: string;
}

const ALL_KEYS: (keyof RadarChartProps['data'])[] = [
    'vocabularyMastery',
    'grammarAccuracy',
    'sentenceStructure',
    'listening',
    'speaking',
    'writing',
];

const DIMENSION_MAP: Record<string, string> = {
    vocabularyMastery: 'vocabulary',
    grammarAccuracy: 'grammar',
    sentenceStructure: 'sentence',
    listening: 'listening',
    speaking: 'speaking',
    writing: 'writing',
};

const CEFR_COLORS: Record<string, string> = {
    A1: '#16a34a', A2: '#22c55e',
    B1: '#ea580c', B2: '#f97316',
    C1: '#dc2626', C2: '#b91c1c',
};

const RadarChart: React.FC<RadarChartProps> = ({
    data, labels, size = 300, dimensionDetails, confidence, dataPoints, overallCefr,
}) => {
    const { t } = useTranslation('knowledgeGraph');
    const center = size / 2;
    const radius = size / 2 - 30;

    const allAxes = ALL_KEYS.map((key, i) => {
        const dimKey = DIMENSION_MAP[key] as keyof NonNullable<RadarChartProps['dimensionDetails']>;
        const detail = dimensionDetails?.[dimKey];
        return {
            key,
            label: labels[i],
            value: data[key],
            isNull: data[key] === null,
            cefr: detail?.cefr || null,
            trend: detail?.trend || null,
        };
    });

    const numAxes = allAxes.length;
    const angleStep = (2 * Math.PI) / numAxes;

    const getPoint = (index: number, value: number) => {
        const angle = angleStep * index - Math.PI / 2;
        return {
            x: center + radius * value * Math.cos(angle),
            y: center + radius * value * Math.sin(angle),
        };
    };

    const rings = [0.2, 0.4, 0.6, 0.8, 1.0];

    const activeIndices = allAxes
        .map((entry, i) => ({ ...entry, index: i }))
        .filter(e => !e.isNull);

    let dataPath = '';
    if (activeIndices.length >= 3) {
        const dataPoints = activeIndices.map(e => getPoint(e.index, e.value as number));
        dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
    }

    const axisEndpoints = allAxes.map((_, i) => getPoint(i, 1));

    const ariaLabel = allAxes
        .map(e => e.isNull ? `${e.label}: unassessed` : `${e.label}: ${Math.round((e.value as number) * 100)}%${e.cefr ? ` (${e.cefr})` : ''}`)
        .join(', ');

    const hasCefrDetails = !!dimensionDetails;

    return (
        <div className="radar-chart">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={ariaLabel}>
                {/* Grid rings */}
                {rings.map((r, ri) => {
                    const ringPoints = allAxes.map((_, i) => getPoint(i, r));
                    const ringPath = ringPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
                    return (
                        <path
                            key={ri}
                            d={ringPath}
                            fill="none"
                            stroke="var(--color-border-subtle, #e2e8f0)"
                            strokeWidth={r === 1 ? 1 : 0.5}
                            opacity={0.5}
                        />
                    );
                })}

                {/* Axis lines */}
                {axisEndpoints.map((ep, i) => (
                    <line
                        key={i}
                        x1={center}
                        y1={center}
                        x2={ep.x}
                        y2={ep.y}
                        stroke="var(--color-border-subtle, #e2e8f0)"
                        strokeWidth={0.5}
                        opacity={0.5}
                        strokeDasharray={allAxes[i].isNull ? '4 3' : 'none'}
                    />
                ))}

                {/* Data area */}
                {dataPath && (
                    <path
                        d={dataPath}
                        fill="rgba(102, 126, 234, 0.15)"
                        stroke="var(--color-primary, #667eea)"
                        strokeWidth="2"
                    />
                )}

                {/* Data points with CEFR color */}
                {activeIndices.map(e => {
                    const p = getPoint(e.index, e.value as number);
                    const dotColor = e.cefr ? CEFR_COLORS[e.cefr] || 'var(--color-primary, #667eea)' : 'var(--color-primary, #667eea)';
                    return (
                        <circle
                            key={e.index}
                            cx={p.x}
                            cy={p.y}
                            r={hasCefrDetails ? 3.5 : 4}
                            fill={dotColor}
                            stroke={hasCefrDetails ? 'white' : 'none'}
                            strokeWidth={hasCefrDetails ? 1.5 : 0}
                        />
                    );
                })}

                {/* Center CEFR level */}
                {overallCefr && (
                    <>
                        <text
                            x={center}
                            y={center - 6}
                            textAnchor="middle"
                            dominantBaseline="central"
                            className="radar-chart__center-level"
                            fill={CEFR_COLORS[overallCefr] || 'var(--color-text-primary)'}
                        >
                            {overallCefr}
                        </text>
                        <text
                            x={center}
                            y={center + 16}
                            textAnchor="middle"
                            dominantBaseline="central"
                            className="radar-chart__center-label"
                        >
                            {t('estimatedLevel')}
                        </text>
                    </>
                )}

                {/* Labels + CEFR levels */}
                {axisEndpoints.map((_, i) => {
                    const angle = angleStep * i - Math.PI / 2;
                    const labelRadius = radius + 24;
                    const lx = center + labelRadius * Math.cos(angle);
                    const ly = center + labelRadius * Math.sin(angle);
                    const anchor = Math.abs(Math.cos(angle)) < 0.1 ? 'middle' :
                        Math.cos(angle) > 0 ? 'start' : 'end';
                    const isUnassessed = allAxes[i].isNull;
                    const cefr = allAxes[i].cefr;

                    return (
                        <text
                            key={i}
                            x={lx}
                            y={ly}
                            textAnchor={anchor}
                            dominantBaseline="central"
                            className={`radar-chart__label${isUnassessed ? ' radar-chart__label--unassessed' : ''}`}
                        >
                            {allAxes[i].label}
                            {cefr && (
                                <tspan
                                    x={lx}
                                    dy={13}
                                    fontSize={8}
                                    fontWeight={700}
                                    fill={CEFR_COLORS[cefr] || 'var(--color-text-secondary)'}
                                >
                                    {cefr}
                                </tspan>
                            )}
                        </text>
                    );
                })}
            </svg>

            {/* Confidence info */}
            {confidence != null && dataPoints != null && (
                <div className="radar-chart__confidence">
                    {t('confidence', { pct: (confidence * 100).toFixed(0), count: dataPoints })}
                </div>
            )}
        </div>
    );
};

export default RadarChart;
